import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Smartphone, RefreshCw, CloudOff } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import EmptyState from '../components/EmptyState.jsx';
import { api } from '../services/api.js';
import { useStorage } from '../hooks/useStorage.js';
import { useFocusRefresh } from '../hooks/useFocusRefresh.js';
import { useToast } from '../contexts/ToastContext.jsx';
import { formatDate, detectPlatform } from '../lib/utils.js';
import { localDB } from '../services/db.js';

export default function Devices() {
  const toast = useToast();
  const [name, setName] = useState('');
  const [type, setType] = useState(detectPlatform());
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);

  const { data: localDevices, loading: localLoading, refresh: refreshLocal, saveItem, removeItem } = useStorage('devices');

  const loadDevices = useCallback(async () => {
    await refreshLocal();
    setError(null);

    if (navigator.onLine) {
      setSyncing(true);
      try {
        const res = await api.listDevices();
        if (res?.data?.items) {
          const apiItems = res.data.items;
          const apiIds = new Set(apiItems.map(d => d.id));

          // Prune deleted devices from local
          const currentLocal = await localDB.getAll('devices');
          for (const device of currentLocal) {
            if (!apiIds.has(device.id)) {
              await localDB.delete('devices', device.id);
            }
          }

          // Save/Update
          for (const device of apiItems) {
            await localDB.put('devices', device);
          }

          await refreshLocal();
        }
      } catch (err) {
        console.warn('[Devices] Background fetch failed:', err);
        setError(err.message);
      } finally {
        setSyncing(false);
      }
    }
  }, [refreshLocal]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  useFocusRefresh(loadDevices);

  const items = localDevices || [];

  const register = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.registerDevice({ deviceName: name, deviceType: type, platform: detectPlatform() });
      setName('');
      toast.success('Device registered');
      loadDevices();
    } catch (err) { toast.error(err.message); }
  };

  const remove = async (id) => {
    if (!confirm('Remove this device?')) return;
    try {
      await removeItem(id);
      await api.deleteDevice(id);
      toast.info('Device removed');
      loadDevices();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div>
      <PageHeader
        title="Devices"
        subtitle="Track every device that connects to your account"
        actions={
          <div className="flex items-center gap-2">
            {syncing && (
              <div className="flex items-center gap-1 text-amber-500 text-xs font-medium bg-amber-500/10 px-2 py-1 rounded-md">
                <RefreshCw size={12} className="animate-spin" />
                <span>Syncing...</span>
              </div>
            )}
            <button className="btn-ghost" onClick={loadDevices}><RefreshCw size={14} /> Refresh</button>
          </div>
        }
      />

      <form onSubmit={register} className="card mb-4 flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label className="label">Device name</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Pixel 9" />
        </div>
        <div className="sm:w-40">
          <label className="label">Type</label>
          <select className="input" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="android">Android</option>
            <option value="ios">iOS</option>
            <option value="macos">macOS</option>
            <option value="windows">Windows</option>
            <option value="linux">Linux</option>
            <option value="web">Web</option>
          </select>
        </div>
        <button className="btn-primary self-stretch sm:self-end" type="submit"><Plus size={14} /> Add</button>
      </form>

      {localLoading && syncing && items.length === 0 && <div className="card text-sm text-zinc-400">Loading devices...</div>}
      {error && items.length === 0 && <div className="card text-sm text-rose-300">Error: {error}</div>}
      {!localLoading && items.length === 0 && (
        <EmptyState icon={Smartphone} title="No devices yet" description="Register the devices you want to sync from." />
      )}

      <ul className="space-y-2">
        {items.map((d) => (
          <li key={d.id} className="card flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold">{d.device_name}</div>
              <div className="text-xs text-zinc-500">Last seen {formatDate(d.last_seen)}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="chip">{d.device_type || d.platform || 'unknown'}</span>
              <button className="btn-danger" onClick={() => remove(d.id)} title="Remove"><Trash2 size={14} /></button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
